import { PatientClientName } from "@components/PatientClientName";
import type { Entity } from "@entities/entitiesClient";
import {
  MIN_QUERY_LENGTH,
  useEntityTypeahead,
} from "@entities/entitiesQueries";
import { cn, Input, useListboxNav } from "@scribenote/brian-design-system";
import { formatDate } from "@utils/format-date";
import { useId, useState } from "react";

/** `useListboxNav` swallows printable keys and Space, so only these reach it from a text input. */
const LISTBOX_NAV_KEYS = new Set([
  "ArrowDown",
  "ArrowUp",
  "Home",
  "End",
  "Enter",
  "Escape",
]);

const ENTITY_SEARCH_LIMIT = 20;

/** `exclude` hides the other side's pick so a patient can't be merged into itself. */
export const PatientPicker = ({
  label,
  value,
  onChange,
  exclude,
  "data-testid": testId,
}: {
  label: string;
  value: Entity | null;
  onChange: (entity: Entity | null) => void;
  exclude?: string;
  "data-testid"?: string;
}) => {
  const inputId = useId();
  const [query, setQuery] = useState("");

  const { data, isFetching, ready, trimmed } = useEntityTypeahead(query, [
    "patient",
  ]);
  const results = (data ?? []).filter((entity) => entity.id !== exclude);

  const choose = (entity: Entity) => {
    onChange(entity);
    setQuery("");
  };

  const { activeIndex, setActiveIndex, handleKeyDown } = useListboxNav({
    itemCount: results.length,
    getLabel: (index) => results[index]?.name ?? "",
    onSelect: (index) => {
      const entity = results[index];
      if (entity) choose(entity);
    },
  });

  if (value) {
    return (
      <div className="flex flex-col gap-1" data-testid={testId}>
        <span className="text-annotation-caps text-annotation">{label}</span>
        <div className="flex items-center justify-between gap-2 rounded-paper border border-rule bg-card px-3 py-2">
          <span className="min-w-0 truncate text-sm font-medium text-ink">
            <PatientClientName
              patientName={value.name}
              clientName={value.clientName}
            />
          </span>
          <button
            type="button"
            onClick={() => onChange(null)}
            data-testid={testId ? `${testId}-change` : undefined}
            className="shrink-0 text-xs font-medium text-accent hover:underline"
          >
            Change
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1" data-testid={testId}>
      <label className="text-annotation-caps text-annotation" htmlFor={inputId}>
        {label}
      </label>
      <Input
        id={inputId}
        type="text"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setActiveIndex(0);
        }}
        onKeyDown={(e) => {
          if (LISTBOX_NAV_KEYS.has(e.key)) handleKeyDown(e);
        }}
        placeholder="Search patients"
        role="combobox"
        aria-expanded={results.length > 0}
        data-testid={testId ? `${testId}-input` : undefined}
      />
      <ul
        role="listbox"
        aria-label={label}
        className="flex max-h-56 min-w-0 flex-col overflow-y-auto rounded-paper border border-rule bg-card"
      >
        {results.length === 0 ? (
          <li className="px-3 py-4 text-center text-sm text-annotation">
            {trimmed.length < MIN_QUERY_LENGTH
              ? "Give me at least two letters."
              : isFetching || !ready
                ? "Looking."
                : "Nothing by that name."}
          </li>
        ) : (
          results.map((entity, index) => {
            const active = index === activeIndex;
            return (
              <li key={entity.id} className="min-w-0">
                <button
                  type="button"
                  role="option"
                  aria-selected={active}
                  onClick={() => choose(entity)}
                  onMouseMove={() => setActiveIndex(index)}
                  data-testid={
                    testId ? `${testId}-option-${entity.id}` : undefined
                  }
                  className={cn(
                    "flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm",
                    "transition-colors duration-150 ease-paper motion-reduce:transition-none",
                    active ? "bg-recess text-ink" : "text-ink-body",
                  )}
                >
                  <span className="min-w-0 flex-1 truncate">
                    <PatientClientName
                      patientName={entity.name}
                      clientName={entity.clientName}
                    />
                  </span>
                  {entity.createdAt && (
                    <span className="shrink-0 text-xs text-annotation">
                      {formatDate(entity.createdAt)}
                    </span>
                  )}
                </button>
              </li>
            );
          })
        )}
      </ul>
      {(data?.length ?? 0) >= ENTITY_SEARCH_LIMIT && (
        <p className="px-1 text-xs text-annotation">
          Showing the first {ENTITY_SEARCH_LIMIT} matches — narrow the search if
          the patient isn't listed.
        </p>
      )}
    </div>
  );
};
